// Shared pan/zoom + gesture handling for the scatter plots, so the value/volume
// and momentum scatters use one implementation. The scatter supplies base
// (unzoomed) coordinates; this owns the reactive scale/pan and the wheel/pointer
// gestures. `getW` reads the current plot width; `h` is the fixed plot height.
export class PanZoom {
  scale = $state(1);
  panX = $state(0);
  panY = $state(0);
  dragging = $state(false);
  dragMoved = false; // set during a drag so a click can be suppressed afterwards

  #getW: () => number;
  #h: number;
  #sx = 0;
  #sy = 0;
  #spx = 0;
  #spy = 0;

  constructor(getW: () => number, h: number) {
    this.#getW = getW;
    this.#h = h;
  }

  zoomAt(cx: number, cy: number, factor: number): void {
    const next = Math.min(12, Math.max(0.6, this.scale * factor));
    if (next === this.scale) return;
    const bx = (cx - this.panX) / this.scale;
    const by = (cy - this.panY) / this.scale;
    this.panX = cx - bx * next;
    this.panY = cy - by * next;
    this.scale = next;
    this.clampPan();
  }

  clampPan(): void {
    // Don't let the plot be dragged entirely off-view.
    const W = this.#getW();
    const w = W * this.scale;
    const h = this.#h * this.scale;
    this.panX = Math.min(W * 0.5, Math.max(W * 0.5 - w, this.panX));
    this.panY = Math.min(this.#h * 0.5, Math.max(this.#h * 0.5 - h, this.panY));
  }

  reset(): void {
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
  }

  // Arrow fields so `this` stays bound when passed straight to event handlers.
  onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    this.zoomAt(e.clientX - r.left, e.clientY - r.top, e.deltaY < 0 ? 1.18 : 1 / 1.18);
  };
  onPointerDown = (e: PointerEvent): void => {
    this.dragging = true;
    this.dragMoved = false;
    this.#sx = e.clientX;
    this.#sy = e.clientY;
    this.#spx = this.panX;
    this.#spy = this.panY;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  onPointerMove = (e: PointerEvent): void => {
    if (!this.dragging) return;
    const dx = e.clientX - this.#sx;
    const dy = e.clientY - this.#sy;
    if (Math.abs(dx) + Math.abs(dy) > 3) this.dragMoved = true;
    this.panX = this.#spx + dx;
    this.panY = this.#spy + dy;
    this.clampPan();
  };
  onPointerUp = (e: PointerEvent): void => {
    this.dragging = false;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  };
}
